import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Row, Col, Input, Button, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  selectProjectList,
  setProjectList,
  setCurrentProject,
} from "../../../store/slices/projects";
import ApiProjects from "../../../api/projects";
import "./project-create-modal.css";
import ProjectKeyColorPicker from "../../../components/color-picker/project-key-color-picker";
import { useTranslation } from "react-i18next";
import { normalizeCode, makeCodeFromTitle } from "../../../common/utils/projectCode";
import { normalizeHex } from "../../../common/utils/color";
import useNotification from "../../../components/notification/notification";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";

// const { Option } = Select;

export const ProjectCreateModal = memo(({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const list = useSelector(selectProjectList) || [];

  const [isCodeManual, setIsCodeManual] = useState(false);
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [keyColor, setKeyColor] = useState("#A3F1CB");
  const navigate = useProjectNavigate();
  const { openNotification } = useNotification();

  const titleWatch = Form.useWatch("title", form);
  useEffect(() => {
    if (!open) return;
    if (!isCodeManual) {
      const auto = makeCodeFromTitle(titleWatch || "");
      form.setFieldsValue({ code: auto });
    }
  }, [open, isCodeManual, titleWatch, form]);

  const codeWatch = Form.useWatch("code", form);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ title: "", code: "", description: "", copyFromProjectId: undefined });
      setKeyColor("#A3F1CB");
      setIsCodeManual(false);
    }
  }, [open, form]);

  const isTitleValid = (titleWatch || "").trim().length > 0;
  const isCodeValid = normalizeCode(codeWatch || "").length > 0;
  const createDisabled = creating || !isTitleValid || !isCodeValid;

  const handleCreate = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        title: values.title.trim(),
        code: normalizeCode(values.code),
        description: values.description ?? "",
        color: normalizeHex(keyColor) || "#A3F1CB",
        ...(values.copyFromProjectId ? { copyFromProjectId: values.copyFromProjectId } : {}),
      };

      setCreating(true);
      const { data } = await ApiProjects.create(payload);
      const created = data?.project || data;

      dispatch(setProjectList([created, ...list]));
      dispatch(setCurrentProject(created));
      openNotification({ type: "success", message: "Project has been created" });
      onClose?.();
      navigate("/dashboard");
    } catch (e) {
      console.error("Failed to create project:", e);

      const msg =
        e?.response?.data?.error ||
        e?.message ||
        "Failed to create project";
      openNotification({ type: "error", message: msg })
    } finally {
      setCreating(false);
    }
  }, [form, keyColor, list, dispatch, onClose]);

  const projectOptions = useMemo(
    () =>
      [...list]
        .sort((a, b) => (a.title || "").localeCompare(b.title || ""))
        .map((p) => ({ label: p.title, value: p.id })),
    [list]
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={578}
      className="project-create-modal"
      destroyOnHidden
      closable={false}
    >
      <div className="pcm-header">
        <div className="pcm-title">New project configuration</div>
        <div className="pcm-subtitle">Write basic information about new project.</div>
      </div>
      <div className="pcm-divider" />

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changed) => {
          if (Object.prototype.hasOwnProperty.call(changed, "code")) {
            setIsCodeManual(true);
            form.setFieldsValue({ code: normalizeCode(changed.code) });
          }
        }}
      >
        <div className="pcm-body">
          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                label="Project name"
                name="title"
                rules={[
                  { required: true, message: "Required" },
                  { max: 255, message: "Too long" },
                ]}
                className="pcm-form-item"
              >
                <Input placeholder={t("project-management.name_ph")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Project ID"
                name="code"
                rules={[
                  { required: true, message: "Required" },
                  { max: 3, message: "Max 3 chars" },
                ]}
                className="pcm-form-item"
              >
                <Input maxLength={3} placeholder={t("project-management.code_ph")} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Project description"
            name="description"
            rules={[{ max: 5000, message: "Too long" }]}
            className="pcm-form-item"
          >
            <Input.TextArea rows={6} placeholder={t("project-management.description_ph")} allowClear />
          </Form.Item>

          <Form.Item label="Key color" style={{ width: "100%" }}>
            <ProjectKeyColorPicker
              value={keyColor}
              onChange={setKeyColor}
              codeText={(form.getFieldValue("code") || "PRJ").toUpperCase().slice(0, 3)}
            />
          </Form.Item>

          <Form.Item
            label="Copy settings and content from"
            name="copyFromProjectId"
            className="pcm-form-item"
          >
            <Select
              allowClear
              placeholder="Select project"
              options={projectOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </div>

        <div className="pcm-divider" />

        <div className="pcm-actions">
          <Button size="large" onClick={onClose}>Cancel</Button>
          <Button size="large" type="primary" onClick={handleCreate} disabled={createDisabled} loading={creating}>
            Create
          </Button>
        </div>
      </Form>
    </Modal>
  );
});

export default ProjectCreateModal;